import * as THREE from 'three';
import type { Engine } from '../core/Engine';
import { CameraModeId } from '../camera/CameraMode';
import { DevOrbitCameraMode } from '../camera/modes/DevOrbitCameraMode';
import {
  createEmptyCourseDocument,
  createEmptyHoleDocument,
  cloneCourseDocument,
  newEditorId,
  type CourseDocument,
  type HoleDocument,
} from '../course/CourseDocument';
import { createDemoCourseDocument, compileCourseDocument } from '../course/CourseCompiler';
import { parseCourseDocumentJson } from '../course/CourseDocumentValidator';
import { COURSE_EDITOR_STORAGE_KEY } from '../course/CourseLibrary';
import { CourseEditorDraftRepository } from '../course/CourseEditorDraftRepository';
import { CommandHistory } from '../course/CommandHistory';
import { CourseEditorHud, type EditorTool, type SelectedObjectSummary } from './CourseEditorHud';
import {
  applyTerrainBrush,
  documentFromHeightField,
  emptyTerrainHeightMap,
  heightFieldFromDocument,
  type TerrainBrushTool,
} from '../course/TerrainSculpting';
import { Units } from '../utils/Units';
import { hitTestRegions, isPointInRegion, type Region } from '../utils/geometry';
import {
  circleToPolygon,
  processFreehandShape,
  processPolygonShape,
} from '../utils/shapeProcessing';
import { ShotSimulation } from '../simulation/ShotSimulation';
import { BallFollowCameraMode } from '../camera/modes/BallFollowCameraMode';

export interface CourseEditorOptions {
  onReturnHome?: () => void;
}

export class CourseEditor {
  private document: CourseDocument = createDemoCourseDocument();
  private currentHoleIndex = 0;
  private tool: EditorTool = 'select';
  private selectedId: string | null = null;
  private message = '';
  private history = new CommandHistory();
  private root = new THREE.Group();
  private hud: CourseEditorHud;
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private drawing: THREE.Vector3[] = [];
  private drawingSurface: 'fairway' | 'green' | 'rough' | 'bunker' | 'water' = 'fairway';
  private drawingMode: 'freehand' | 'polygon' = 'polygon';
  private freehandDrawing = false;
  private showColliders = false;
  private dragLast: THREE.Vector3 | null = null;
  private draggingSelected = false;
  private brushPreview: THREE.Mesh | null = null;
  private brushSettings = {
    radius: 18,
    strength: 0.45,
    falloff: 'smooth' as const,
    slopeDirection: { x: 1, z: 0 },
  };
  private simulation: ShotSimulation | null = null;
  private playCourse: ReturnType<typeof compileCourseDocument> | null = null;
  private readonly drafts = new CourseEditorDraftRepository();
  private autosaveTimer: number | null = null;
  private lastSavedAt = 'Never';
  private unsavedChanges = false;
  private lastPick: { x: number; z: number; ids: string[]; index: number; at: number } | null =
    null;
  constructor(
    private engine: Engine,
    private container: HTMLElement,
    private readonly options: CourseEditorOptions = {},
  ) {
    this.root.name = 'CourseEditor';
    engine.scene.add(this.root);
    this.hud = new CourseEditorHud(container, {
      setTool: (t) => {
        this.tool = t;
        if (['fairway', 'green', 'rough', 'bunker', 'water'].includes(t))
          this.drawingSurface = t as typeof this.drawingSurface;
        if (t === 'freehand') this.drawingMode = 'freehand';
        if (t === 'polygon') this.drawingMode = 'polygon';
        if (t === 'showColliders') this.showColliders = !this.showColliders;
        this.drawing = [];
      },
      newCourse: () => this.replaceDocument(createEmptyCourseDocument()),
      addHole: () =>
        this.mutate((d) => {
          d.holes.push(createEmptyHoleDocument(d.holes.length + 1));
          this.currentHoleIndex = d.holes.length - 1;
        }),
      previousHole: () => {
        this.currentHoleIndex = Math.max(0, this.currentHoleIndex - 1);
        this.render();
      },
      nextHole: () => {
        this.currentHoleIndex = Math.min(this.document.holes.length - 1, this.currentHoleIndex + 1);
        this.render();
      },
      save: () => {
        localStorage.setItem(COURSE_EDITOR_STORAGE_KEY, JSON.stringify(this.document));
        const snapshot = this.drafts.saveDraft(this.document);
        this.lastSavedAt = new Date(snapshot.savedAt).toLocaleTimeString();
        this.unsavedChanges = false;
        this.message = `Saved. Last saved ${this.lastSavedAt}.`;
        this.render();
      },
      load: () => this.loadLocal(),
      exportJson: () => this.exportJson(),
      importJson: (f) => void this.importJson(f),
      playTest: () => void this.playTest(),
      updateHole: (p) => this.mutate((d) => Object.assign(d.holes[this.currentHoleIndex], p)),
      updateSelected: (p) => this.updateSelected(p),
      undo: () => this.undo(),
      redo: () => this.redo(),
      finishDrawing: () => this.finishDrawing(),
      returnHome: this.options.onReturnHome
        ? () => {
            if (this.unsavedChanges) this.drafts.saveDraft(this.document);
            localStorage.setItem(COURSE_EDITOR_STORAGE_KEY, JSON.stringify(this.document));
            this.options.onReturnHome?.();
          }
        : undefined,
    });
    this.bind();
    this.setupCamera();
    const draft = this.drafts.loadDraft();
    if (draft) {
      this.document = cloneCourseDocument(draft.document);
      this.lastSavedAt = new Date(draft.savedAt).toLocaleTimeString();
      this.message = `Recovered autosave from ${this.lastSavedAt}.`;
    }
    this.render();
  }
  private get hole(): HoleDocument | undefined {
    return this.document.holes[this.currentHoleIndex];
  }
  private setupCamera(): void {
    const cam = new DevOrbitCameraMode(
      this.engine.renderer.domElement,
      new THREE.Vector3(40, 0, 260),
      new THREE.Vector3(40, 250, 260),
    );
    this.engine.cameras.register(cam);
    this.engine.cameras.setActive(CameraModeId.DevOrbit);
  }
  private bind(): void {
    const el = this.engine.renderer.domElement;
    el.addEventListener('pointerdown', this.onPointer);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('keydown', this.onKey);
  }
  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.drawing = [];
      this.tool = 'select';
      this.hud.setActiveTool('select');
      this.render();
    }
    if (e.key === 'Enter') this.finishDrawing();
    if (e.key.toLowerCase() === 'c' && this.selectedId) this.convertSelectedCircleToPolygon();
    if (e.key === 'Delete' || e.key === 'Backspace') this.deleteSelected();
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && e.shiftKey) this.redo();
    else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') this.undo();
    else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') this.redo();
  };
  private onPointer = (e: PointerEvent): void => {
    if (this.simulation) return;
    const p = this.pointerToWorld(e);
    const hole = this.hole;
    if (!p || !hole) return;
    if (this.tool === 'select' || this.tool === 'editShape') {
      this.selectedId = this.pickId(e);
      if (this.selectedId) {
        this.history.capture(this.document);
        this.dragLast = p.clone();
        this.draggingSelected = true;
        this.engine.renderer.domElement.setPointerCapture?.(e.pointerId);
      }
      this.render();
      return;
    }
    if (this.isTerrainTool(this.tool)) {
      this.applyBrushAt(p);
      return;
    }
    if (this.tool === 'measure') {
      this.message = `Point: ${p.x.toFixed(1)}, ${p.z.toFixed(1)}; elevation ${(this.document.terrain ? heightFieldFromDocument(this.document.terrain).sampleHeight(p.x, p.z) : 0).toFixed(2)}m`;
      this.render();
      return;
    }
    if (this.tool === 'validate') {
      this.message = this.validateDesign().join('\n') || 'No severe validation warnings.';
      this.render();
      return;
    }
    if (this.tool === 'delete') {
      this.selectedId = this.pickId(e);
      this.deleteSelected();
      return;
    }
    if (['fairway', 'green', 'rough', 'bunker', 'water'].includes(this.tool))
      this.drawingSurface = this.tool as typeof this.drawingSurface;
    if (
      ['fairway', 'green', 'rough', 'bunker', 'water', 'freehand', 'polygon'].includes(this.tool)
    ) {
      this.handleShapePointerDown(e, p);
      return;
    }
    this.mutate((d) => {
      const h = d.holes[this.currentHoleIndex];
      const pos = { x: p.x, y: p.y, z: p.z };
      if (this.tool === 'tee')
        h.teeBoxes.push({ id: newEditorId('tee'), name: 'Back', position: pos });
      else if (this.tool === 'pin')
        h.pins.push({ id: newEditorId('pin'), label: 'Default', position: pos });
      else if (this.tool === 'tree')
        h.vegetation.push({
          id: newEditorId('tree'),
          kind: 'tree',
          position: pos,
          scale: 1,
          rotationY: 0,
        });
      else if (this.tool === 'vegetationPaint') {
        for (let i = 0; i < 5; i++) {
          const a = Math.random() * Math.PI * 2;
          const r = Math.sqrt(Math.random()) * 14;
          h.vegetation.push({
            id: newEditorId('tree'),
            kind: 'tree',
            position: { x: p.x + Math.cos(a) * r, y: 0, z: p.z + Math.sin(a) * r },
            scale: 0.8 + Math.random() * 0.5,
            rotationY: Math.random() * Math.PI * 2,
          });
        }
      } else if (this.tool === 'rock')
        h.props.push({ id: newEditorId('rock'), kind: 'rock', position: pos, radius: 2, scale: 1 });
    });
  };

  private handleShapePointerDown(e: PointerEvent, p: THREE.Vector3): void {
    if (this.drawingMode === 'freehand' || this.tool === 'freehand') {
      this.drawingMode = 'freehand';
      this.freehandDrawing = true;
      this.drawing = [p.clone()];
      this.history.capture(this.document);
      return;
    }
    this.drawingMode = 'polygon';
    this.drawing.push(p.clone());
    if (this.drawing.length >= 3 && e.detail >= 2) {
      this.completeDrawnShape(processPolygonShape(this.drawing.map((v) => ({ x: v.x, z: v.z }))));
    } else this.render();
  }

  private finishDrawing(): void {
    if (this.drawing.length < 3) {
      this.message = 'Add at least three points before finishing the shape.';
      this.render();
      return;
    }
    this.completeDrawnShape(processPolygonShape(this.drawing.map((v) => ({ x: v.x, z: v.z }))));
  }

  private completeDrawnShape(result: ReturnType<typeof processPolygonShape>): void {
    if (!result.region) {
      this.message = result.warning ?? 'Invalid outline.';
      this.drawing = [];
      this.render();
      return;
    }
    this.mutate((d) => {
      const h = d.holes[this.currentHoleIndex];
      const item = { id: newEditorId(this.drawingSurface), region: result.region! };
      if (this.drawingSurface === 'fairway') h.fairways.push(item);
      else if (this.drawingSurface === 'green') h.greens.push(item);
      else if (this.drawingSurface === 'rough') h.roughRegions.push(item);
      else if (this.drawingSurface === 'bunker') h.bunkers.push(item);
      else if (this.drawingSurface === 'water') h.waterRegions.push(item);
      else h.fairways.push(item);
      this.selectedId = item.id;
    });
    this.message = `${this.drawingSurface} polygon created.`;
    this.drawing = [];
  }
  private onPointerMove = (e: PointerEvent): void => {
    const p = this.pointerToWorld(e);
    if (p && this.isTerrainTool(this.tool)) this.updateBrushPreview(p);
    if (p && this.freehandDrawing) {
      this.drawing.push(p.clone());
      this.render();
    }
    if (!this.draggingSelected || !this.selectedId || this.simulation) return;
    if (!p || !this.dragLast) return;
    const dx = p.x - this.dragLast.x;
    const dz = p.z - this.dragLast.z;
    this.translateSelected(dx, dz);
    this.dragLast.copy(p);
    this.render();
  };
  private onPointerUp = (): void => {
    if (this.freehandDrawing) {
      this.freehandDrawing = false;
      this.completeDrawnShape(processFreehandShape(this.drawing.map((v) => ({ x: v.x, z: v.z }))));
    }
    this.draggingSelected = false;
    this.dragLast = null;
  };
  private pointerToWorld(e: PointerEvent): THREE.Vector3 | null {
    const r = this.engine.renderer.domElement.getBoundingClientRect();
    this.pointer.set(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -(((e.clientY - r.top) / r.height) * 2 - 1),
    );
    this.raycaster.setFromCamera(this.pointer, this.engine.cameras.activeCamera);
    const out = new THREE.Vector3();
    const terrainMeshes: THREE.Object3D[] = [];
    this.root.traverse((obj) => {
      if (obj.name === 'EditorTerrainMesh') terrainMeshes.push(obj);
    });
    const hits = this.raycaster.intersectObjects(terrainMeshes, false);
    if (hits[0]) return hits[0].point.clone();
    return this.raycaster.ray.intersectPlane(this.ground, out);
  }
  private pickId(e: PointerEvent): string | null {
    const p = this.pointerToWorld(e);
    if (!p || !this.hole) return null;
    const candidates = this.pickCandidates(this.hole);
    const point = { x: p.x, z: p.z };
    const regionHits = hitTestRegions(point, candidates.regions, {
      edgeTolerance: 4,
      vertexTolerance: 2.8,
    });
    const markerHits = candidates.markers
      .map((candidate) => ({
        ...candidate,
        distance: Math.hypot(candidate.x - p.x, candidate.z - p.z),
      }))
      .filter((candidate) => candidate.distance <= candidate.radius)
      .sort((a, b) => a.distance - b.distance);
    const ids = [...markerHits.map((hit) => hit.id), ...regionHits.map((hit) => hit.id)];
    if (ids.length === 0) return null;
    const now = performance.now();
    const repeat =
      this.lastPick &&
      now - this.lastPick.at < 900 &&
      Math.hypot(this.lastPick.x - point.x, this.lastPick.z - point.z) < 2.5 &&
      ids.join('|') === this.lastPick.ids.join('|');
    const index = repeat ? (this.lastPick!.index + 1) % ids.length : 0;
    this.lastPick = { ...point, ids, index, at: now };
    return ids[index];
  }
  private pickCandidates(hole: HoleDocument): {
    markers: { id: string; x: number; z: number; radius: number }[];
    regions: { id: string; region: Region; priority?: number }[];
  } {
    const markers: { id: string; x: number; z: number; radius: number }[] = [];
    const regions: { id: string; region: Region; priority?: number }[] = [];
    hole.teeBoxes.forEach((t) =>
      markers.push({ id: t.id, x: t.position.x, z: t.position.z, radius: 12 }),
    );
    hole.pins.forEach((p) =>
      markers.push({ id: p.id, x: p.position.x, z: p.position.z, radius: 12 }),
    );
    hole.vegetation.forEach((t) =>
      markers.push({ id: t.id, x: t.position.x, z: t.position.z, radius: 12 }),
    );
    hole.props.forEach((p) =>
      markers.push({ id: p.id, x: p.position.x, z: p.position.z, radius: 12 }),
    );
    const addRegions = (items: HoleDocument['greens'], priority: number) =>
      items.forEach((item) => regions.push({ id: item.id, region: item.region, priority }));
    addRegions(hole.waterRegions, 5);
    addRegions(hole.bunkers, 4);
    addRegions(hole.greens, 3);
    addRegions(hole.roughRegions, 2);
    addRegions(hole.fairways, 1);
    return { markers, regions };
  }
  private isTerrainTool(tool: EditorTool): tool is TerrainBrushTool {
    return [
      'raise',
      'lower',
      'smooth',
      'flatten',
      'slope',
      'greenContour',
      'bunkerRecess',
    ].includes(tool);
  }
  private ensureTerrain(): void {
    if (!this.document.terrain) this.document.terrain = emptyTerrainHeightMap();
  }
  private applyBrushAt(p: THREE.Vector3): void {
    this.mutate((d) => {
      this.ensureTerrain();
      const field = heightFieldFromDocument(d.terrain!);
      applyTerrainBrush(field, { x: p.x, z: p.z }, this.tool as TerrainBrushTool, {
        ...this.brushSettings,
        targetHeight: field.sampleHeight(p.x, p.z),
      });
      d.terrain = documentFromHeightField(field);
      for (const h of d.holes) {
        h.vegetation.forEach((t) => {
          t.position.y = field.sampleHeight(t.position.x, t.position.z);
        });
        h.props.forEach((p) => {
          p.position.y = field.sampleHeight(p.position.x, p.position.z);
        });
        h.teeBoxes.forEach((t) => {
          t.position.y = field.sampleHeight(t.position.x, t.position.z);
        });
        h.pins.forEach((pin) => {
          pin.position.y = field.sampleHeight(pin.position.x, pin.position.z);
        });
      }
    });
  }
  private updateBrushPreview(p: THREE.Vector3): void {
    if (!this.brushPreview) return;
    this.brushPreview.position.set(p.x, 0.08, p.z);
    this.brushPreview.scale.setScalar(this.brushSettings.radius);
  }
  private mutate(fn: (doc: CourseDocument) => void): void {
    this.history.capture(this.document);
    fn(this.document);
    this.unsavedChanges = true;
    this.scheduleAutosave();
    this.render();
  }
  private replaceDocument(doc: CourseDocument): void {
    this.document = cloneCourseDocument(doc);
    this.unsavedChanges = false;
    this.currentHoleIndex = 0;
    this.selectedId = null;
    this.render();
  }
  private deleteSelected(): void {
    if (!this.selectedId) return;
    this.mutate((d) => {
      const h = d.holes[this.currentHoleIndex];
      for (const key of [
        'teeBoxes',
        'pins',
        'fairways',
        'greens',
        'roughRegions',
        'bunkers',
        'waterRegions',
        'vegetation',
        'props',
      ] as const)
        h[key] = h[key].filter((x) => x.id !== this.selectedId) as never;
      this.selectedId = null;
    });
  }
  private undo(): void {
    const doc = this.history.undo(this.document);
    if (doc) this.replaceDocument(doc);
  }
  private redo(): void {
    const doc = this.history.redo(this.document);
    if (doc) this.replaceDocument(doc);
  }
  private selectedSummary(): SelectedObjectSummary | null {
    const h = this.hole;
    if (!h || !this.selectedId) return null;
    const id = this.selectedId;
    const tee = h.teeBoxes.find((x) => x.id === id);
    if (tee)
      return {
        id,
        label: 'Tee box',
        x: tee.position.x,
        y: tee.position.y ?? 0,
        z: tee.position.z,
        name: tee.name,
        surface: 'Tee',
      };
    const pin = h.pins.find((x) => x.id === id);
    if (pin)
      return {
        id,
        label: 'Pin',
        x: pin.position.x,
        y: pin.position.y ?? 0,
        z: pin.position.z,
        name: pin.label ?? '',
        surface: 'Green target',
      };
    const placement = [...h.vegetation, ...h.props].find((x) => x.id === id);
    if (placement)
      return {
        id,
        label: placement.kind === 'tree' ? 'Tree' : 'Rock',
        x: placement.position.x,
        y: placement.position.y ?? 0,
        z: placement.position.z,
        radius: placement.radius ?? placement.scale ?? 1,
        surface: 'Prop placement',
      };
    const region = this.findRegion(id);
    if (!region) return null;
    const value = region.item.region;
    if (value.kind === 'circle')
      return {
        id,
        label: region.label,
        x: value.center.x,
        z: value.center.z,
        radius: value.radius,
        surface: region.label,
      };
    const center = this.polygonCenter(value.points);
    return {
      id,
      label: region.label,
      x: center.x,
      z: center.z,
      surface: `${region.label} polygon`,
    };
  }

  private convertSelectedCircleToPolygon(): void {
    const found = this.findRegion(this.selectedId ?? '');
    if (!found || found.item.region.kind !== 'circle') return;
    this.mutate(() => {
      if (found.item.region.kind === 'circle') {
        found.item.region = circleToPolygon(found.item.region.center, found.item.region.radius);
        this.message = 'Converted circle to editable polygon.';
      }
    });
  }

  private updateSelected(patch: Record<string, number | string>): void {
    if (!this.selectedId) return;
    this.mutate(() => this.applySelectedPatch(patch));
  }
  private translateSelected(dx: number, dz: number): void {
    this.applySelectedPatch({ dx, dz });
  }
  private applySelectedPatch(patch: Record<string, number | string>): void {
    const h = this.hole;
    if (!h || !this.selectedId) return;
    const id = this.selectedId;
    const movePoint = (position: { x: number; y?: number; z: number }) => {
      if (typeof patch.dx === 'number') position.x += patch.dx;
      if (typeof patch.dz === 'number') position.z += patch.dz;
      if (typeof patch.x === 'number') position.x = patch.x;
      if (typeof patch.y === 'number') position.y = patch.y;
      if (typeof patch.z === 'number') position.z = patch.z;
    };
    const tee = h.teeBoxes.find((x) => x.id === id);
    if (tee) {
      movePoint(tee.position);
      if (typeof patch.name === 'string') tee.name = patch.name;
      return;
    }
    const pin = h.pins.find((x) => x.id === id);
    if (pin) {
      movePoint(pin.position);
      if (typeof patch.name === 'string') pin.label = patch.name;
      return;
    }
    const placement = [...h.vegetation, ...h.props].find((x) => x.id === id);
    if (placement) {
      movePoint(placement.position);
      if (typeof patch.radius === 'number') {
        placement.radius = patch.radius;
        placement.scale = patch.radius;
      }
      if (typeof patch.name === 'string')
        placement.kind = patch.name === 'rock' ? 'rock' : placement.kind;
      return;
    }
    const region = this.findRegion(id)?.item.region;
    if (!region) return;
    if (region.kind === 'circle') {
      if (typeof patch.dx === 'number') region.center.x += patch.dx;
      if (typeof patch.dz === 'number') region.center.z += patch.dz;
      if (typeof patch.x === 'number') region.center.x = patch.x;
      if (typeof patch.z === 'number') region.center.z = patch.z;
      if (typeof patch.radius === 'number') region.radius = Math.max(1, patch.radius);
    } else {
      const center = this.polygonCenter(region.points);
      const dx =
        typeof patch.dx === 'number'
          ? patch.dx
          : typeof patch.x === 'number'
            ? patch.x - center.x
            : 0;
      const dz =
        typeof patch.dz === 'number'
          ? patch.dz
          : typeof patch.z === 'number'
            ? patch.z - center.z
            : 0;
      region.points.forEach((point) => {
        point.x += dx;
        point.z += dz;
      });
    }
  }
  private findRegion(id: string): { label: string; item: HoleDocument['greens'][number] } | null {
    const h = this.hole;
    if (!h) return null;
    const groups: [string, HoleDocument['greens']][] = [
      ['Fairway', h.fairways],
      ['Green', h.greens],
      ['Rough', h.roughRegions],
      ['Bunker', h.bunkers],
      ['Water', h.waterRegions],
    ];
    for (const [label, items] of groups) {
      const item = items.find((x) => x.id === id);
      if (item) return { label, item };
    }
    return null;
  }
  private polygonCenter(points: { x: number; z: number }[]): { x: number; z: number } {
    if (points.length === 0) return { x: 0, z: 0 };
    return {
      x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
      z: points.reduce((sum, point) => sum + point.z, 0) / points.length,
    };
  }
  private render(): void {
    this.root.clear();
    this.brushPreview = null;
    const h = this.hole;
    if (!h) {
      this.hud.render(h, this.selectedSummary(), this.message, this.editorStatus());
      return;
    }
    const addCircle = (id: string, x: number, z: number, r: number, c: number) => {
      const mesh = new THREE.Mesh(
        new THREE.CircleGeometry(r, 48),
        new THREE.MeshBasicMaterial({
          color: c,
          transparent: true,
          opacity: id === this.selectedId?.toString() ? 0.85 : 0.45,
          side: THREE.DoubleSide,
        }),
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(x, 0.05, z);
      mesh.userData.id = id;
      this.root.add(mesh);
    };
    const terrainField = this.document.terrain
      ? heightFieldFromDocument(this.document.terrain)
      : null;
    const yAt = (x: number, z: number) =>
      (terrainField ? terrainField.sampleHeight(x, z) : 0) + 0.06;
    const addMarker = (id: string, x: number, z: number, c: number) => addCircle(id, x, z, 4, c);
    const addPolygon = (id: string, points: { x: number; z: number }[], c: number) => {
      const shape = new THREE.Shape(points.map((p) => new THREE.Vector2(p.x, p.z)));
      const mesh = new THREE.Mesh(
        new THREE.ShapeGeometry(shape),
        new THREE.MeshBasicMaterial({
          color: c,
          transparent: true,
          opacity: id === this.selectedId ? 0.72 : 0.46,
          side: THREE.DoubleSide,
        }),
      );
      mesh.rotation.x = Math.PI / 2;
      mesh.position.y = 0.07;
      mesh.userData.id = id;
      this.root.add(mesh);
      const line = new THREE.LineLoop(
        new THREE.BufferGeometry().setFromPoints(
          points.map((p) => new THREE.Vector3(p.x, yAt(p.x, p.z), p.z)),
        ),
        new THREE.LineBasicMaterial({ color: id === this.selectedId ? 0xffffff : c }),
      );
      this.root.add(line);
    };
    if (terrainField) {
      const geometry = new THREE.PlaneGeometry(
        420,
        680,
        terrainField.width - 1,
        terrainField.depth - 1,
      );
      geometry.rotateX(-Math.PI / 2);
      const posAttr = geometry.getAttribute('position');
      for (let i = 0; i < posAttr.count; i++)
        posAttr.setY(i, terrainField.sampleHeight(posAttr.getX(i), posAttr.getZ(i)));
      posAttr.needsUpdate = true;
      geometry.computeVertexNormals();
      const mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({ color: 0x315c35, roughness: 0.9, metalness: 0 }),
      );
      mesh.name = 'EditorTerrainMesh';
      this.root.add(mesh);
    }
    if (this.isTerrainTool(this.tool)) {
      this.brushPreview = new THREE.Mesh(
        new THREE.RingGeometry(0.92, 1, 48),
        new THREE.MeshBasicMaterial({
          color: 0xffffaa,
          transparent: true,
          opacity: 0.8,
          side: THREE.DoubleSide,
        }),
      );
      this.brushPreview.rotation.x = -Math.PI / 2;
      this.brushPreview.scale.setScalar(this.brushSettings.radius);
      this.root.add(this.brushPreview);
    }
    h.fairways.forEach((f) =>
      f.region.kind === 'polygon'
        ? addPolygon(f.id, f.region.points, 0x4da35d)
        : addCircle(f.id, f.region.center.x, f.region.center.z, f.region.radius, 0x4da35d),
    );
    h.greens.forEach((g) =>
      g.region.kind === 'circle'
        ? addCircle(g.id, g.region.center.x, g.region.center.z, g.region.radius, 0x64d66e)
        : addPolygon(g.id, g.region.points, 0x64d66e),
    );
    h.roughRegions.forEach((g) =>
      g.region.kind === 'circle'
        ? addCircle(g.id, g.region.center.x, g.region.center.z, g.region.radius, 0x356b31)
        : addPolygon(g.id, g.region.points, 0x356b31),
    );
    h.bunkers.forEach((g) =>
      g.region.kind === 'circle'
        ? addCircle(g.id, g.region.center.x, g.region.center.z, g.region.radius, 0xd9c27a)
        : addPolygon(g.id, g.region.points, 0xd9c27a),
    );
    h.waterRegions.forEach((g) =>
      g.region.kind === 'circle'
        ? addCircle(g.id, g.region.center.x, g.region.center.z, g.region.radius, 0x368bd6)
        : addPolygon(g.id, g.region.points, 0x368bd6),
    );
    h.teeBoxes.forEach((t) => addMarker(t.id, t.position.x, t.position.z, 0xffffff));
    h.pins.forEach((p) => addMarker(p.id, p.position.x, p.position.z, 0xff3333));
    h.vegetation.forEach((t) => addMarker(t.id, t.position.x, t.position.z, 0x164f1b));
    h.props.forEach((p) => addMarker(p.id, p.position.x, p.position.z, 0x777777));
    if (this.drawing.length)
      addPolygon(
        'preview',
        this.drawing.map((v) => ({ x: v.x, z: v.z })),
        0xffffaa,
      );
    if (this.showColliders) {
      h.vegetation.forEach((t) =>
        addCircle(`collider-${t.id}`, t.position.x, t.position.z, 2.2 * (t.scale ?? 1), 0xffaa00),
      );
      h.props.forEach((p) =>
        addCircle(
          `collider-${p.id}`,
          p.position.x,
          p.position.z,
          (p.radius ?? 2) * (p.scale ?? 1),
          0xff6600,
        ),
      );
      h.waterRegions.forEach(
        (w) =>
          w.region.kind === 'polygon' && addPolygon(`hazard-${w.id}`, w.region.points, 0x44ccff),
      );
    }
    this.hud.render(h, this.selectedSummary(), this.message, this.editorStatus());
  }

  private editorStatus(): string {
    const parts = [`Tool: ${this.tool}`];
    parts.push(
      `Drawing: ${this.drawingMode}${this.drawing.length ? ` (${this.drawing.length} points unfinished)` : ''}`,
    );
    if (this.selectedId)
      parts.push(`Selected: ${this.selectedSummary()?.label ?? this.selectedId}`);
    if (this.tool === 'editShape')
      parts.push('Edit Shape: click vertices/edges, drag to move, Delete removes selection');
    if (this.isTerrainTool(this.tool))
      parts.push(
        `Brush: ${this.tool} r=${this.brushSettings.radius}m strength=${this.brushSettings.strength}`,
      );
    parts.push(this.unsavedChanges ? 'Unsaved changes' : `Saved ${this.lastSavedAt}`);
    if (this.showColliders) parts.push('Collider view on');
    return parts.join(' · ');
  }

  private scheduleAutosave(): void {
    if (this.autosaveTimer !== null) window.clearTimeout(this.autosaveTimer);
    this.autosaveTimer = window.setTimeout(() => {
      const snapshot = this.drafts.saveDraft(this.document);
      this.lastSavedAt = new Date(snapshot.savedAt).toLocaleTimeString();
      this.autosaveTimer = null;
    }, 600);
  }
  private loadLocal(): void {
    const saved = localStorage.getItem(COURSE_EDITOR_STORAGE_KEY);
    if (!saved) {
      this.message = 'No saved course found.';
      this.render();
      return;
    }
    const result = parseCourseDocumentJson(saved);
    if (result.document) this.replaceDocument(result.document);
    else {
      this.message = result.errors.join('\n');
      this.render();
    }
  }
  private exportJson(): void {
    const blob = new Blob([JSON.stringify(this.document, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${this.document.id}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  private async importJson(file: File): Promise<void> {
    const result = parseCourseDocumentJson(await file.text());
    if (result.document) this.replaceDocument(result.document);
    else {
      this.message = result.errors.join('\n');
      this.render();
    }
  }
  private async playTest(): Promise<void> {
    this.root.visible = false;
    this.hud.element.style.display = 'none';
    this.playCourse = compileCourseDocument(this.document);
    await this.playCourse.load(this.engine);
    this.simulation = new ShotSimulation(this.engine, this.playCourse, {
      debugHudContainer: this.container,
      buildInfo: {
        version: __APP_VERSION__,
        mode: 'editor play-test',
        controlScheme: 'keyboard + mobile swing',
      },
      showDebugTrail: false,
    });
    this.simulation.start();
    const button = document.createElement('button');
    button.textContent = 'Return to Editor';
    button.style.cssText = 'position:absolute;top:10px;right:10px;z-index:20';
    button.onclick = () => {
      button.remove();
      this.simulation?.dispose();
      this.simulation = null;
      this.playCourse?.unload();
      this.playCourse = null;
      this.root.visible = true;
      this.hud.element.style.display = 'block';
      this.setupCamera();
    };
    this.container.appendChild(button);
    const ballCamera = new BallFollowCameraMode(
      this.simulation.ball,
      () => this.simulation!.currentPinPosition(),
      () => this.simulation!.aimDirection(),
      () => this.simulation!.landingZonePreviewTarget(),
      () => this.simulation!.isGreenReadEnabled(),
      () => this.simulation!.isPuttingLie(),
    );
    this.engine.cameras.register(ballCamera);
    this.engine.cameras.setActive(CameraModeId.BallFollow);
  }
  private validateDesign(): string[] {
    const h = this.hole;
    if (!h) return ['No selected hole.'];
    const warnings: string[] = [];
    if (h.teeBoxes.length === 0) warnings.push('Severe: hole has no tee.');
    if (h.pins.length === 0) warnings.push('Severe: hole has no pin.');
    const pin = h.pins[h.activePinIndex] ?? h.pins[0];
    if (
      pin &&
      !h.greens.some((g) => isPointInRegion({ x: pin.position.x, z: pin.position.z }, g.region))
    )
      warnings.push('Severe: pin is outside the green.');
    const tee = h.teeBoxes[0];
    if (
      tee &&
      (h.bunkers.some((b) => isPointInRegion({ x: tee.position.x, z: tee.position.z }, b.region)) ||
        h.waterRegions.some((w) =>
          isPointInRegion({ x: tee.position.x, z: tee.position.z }, w.region),
        ))
    )
      warnings.push('Severe: tee is inside water or bunker.');
    if (h.fairways.length === 0) warnings.push('Warning: fairway does not connect tee and green.');
    if (h.teeBoxes[0] && pin)
      warnings.push(
        `Tee to pin: ${Units.metersToYards(Math.hypot(pin.position.x - h.teeBoxes[0].position.x, pin.position.z - h.teeBoxes[0].position.z)).toFixed(0)} yd, elevation ${((pin.position.y ?? 0) - (h.teeBoxes[0].position.y ?? 0)).toFixed(1)}m.`,
      );
    return warnings;
  }
  dispose(): void {
    this.hud.dispose();
    this.root.removeFromParent();
    this.engine.renderer.domElement.removeEventListener('pointerdown', this.onPointer);
    if (this.autosaveTimer !== null) window.clearTimeout(this.autosaveTimer);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('keydown', this.onKey);
  }
}
